"use client";

import { useMemo, useState } from "react";
import {
  Map,
  AdvancedMarker,
  InfoWindow,
  useAdvancedMarkerRef,
  useApiIsLoaded,
} from "@vis.gl/react-google-maps";
import { useMapsConfig } from "@/hooks/useMapsConfig";
import { CookieGatedMap } from "@/components/cookie-consent/CookieGatedMap";
import { useCookieServiceAllowed } from "@/hooks/useCookieServiceAllowed";
import { resolveGoogleMapId } from "@/lib/google-maps-constants";
import GoogleMapsApiProvider from "@/components/maps/GoogleMapsApiProvider";

interface MapData {
  lat: number;
  lng: number;
  title: string;
  address: string;
}

export default function GoogleJavaScriptMap({
  className,
  mapData,
}: {
  className?: string;
  mapData: MapData;
}) {
  const { api_key: googleMapsApiKey, map_id, isLoading: mapsConfigLoading } = useMapsConfig();
  const { allowed: mapsConsentAllowed } = useCookieServiceAllowed("google_maps");
  const apiReady = useApiIsLoaded();
  const mapId = useMemo(() => resolveGoogleMapId(map_id), [map_id]);
  const [infoOpen, setInfoOpen] = useState(false);
  const [markerRef, marker] = useAdvancedMarkerRef();

  const canShowMap =
    mapsConsentAllowed && !mapsConfigLoading && Boolean(googleMapsApiKey) && apiReady;

  const infoContent = useMemo(
    () => (
      <div className="max-w-[300px] p-4 font-[family-name:var(--font-inter,sans-serif)]">
        <h3 className="mb-3 border-b-2 border-[#e31e24] pb-2 text-lg font-semibold text-[#1a1a1a]">
          {mapData.title}
        </h3>
        <p className="text-sm leading-relaxed text-[#4f545a]">{mapData.address}</p>
      </div>
    ),
    [mapData.address, mapData.title],
  );

  return (
    <GoogleMapsApiProvider>
    <CookieGatedMap className={className} minHeight="400px">
      {canShowMap ? (
        <Map
          mapId={mapId}
          className={className}
          defaultCenter={{ lat: mapData.lat, lng: mapData.lng }}
          defaultZoom={15}
          gestureHandling="greedy"
          disableDefaultUI={false}
        >
          <AdvancedMarker
            ref={markerRef}
            position={{ lat: mapData.lat, lng: mapData.lng }}
            title={mapData.title}
            onClick={() => setInfoOpen(true)}
          />
          {infoOpen && marker && (
            <InfoWindow anchor={marker} onCloseClick={() => setInfoOpen(false)}>
              {infoContent}
            </InfoWindow>
          )}
        </Map>
      ) : (
        <div className={className} aria-hidden />
      )}
    </CookieGatedMap>
    </GoogleMapsApiProvider>
  );
}
